import {
  ICustomFieldRecordSchema,
  IQuoteOfferingRespSchema,
  IQuoteRespSchema,
  QuoteOfferingGroup,
  QuoteOfferingGroupRequestSchema,
} from '@monetize/types/app';
import { Maybe } from '@monetize/types/common';
import { useCallback, useMemo, useReducer } from 'react';
import {
  buildManageGroupsDraftPlan,
  buildManageGroupsRowPayload,
  buildNewManageGroupsDraftRow,
  getManageGroupsRowStatuses,
  getManageGroupsRowSubtotal,
  getQuoteOfferingDisplayDetail,
  getRootQuoteOfferings,
  hasManageGroupsDraftChanges,
  ManageGroupsDraftRow,
  ManageGroupsOfferingDetail,
  seedManageGroupsDraftRows,
} from './manageQuoteOfferingGroupsUtils';

export type ManageGroupsEditableField =
  | 'shippingAccountId'
  | 'shippingContactId'
  | 'billingAccountId'
  | 'billingContactId';

/** Validation message per payload field, e.g. `{ billingContactId: '…' }` */
export type ManageGroupsRowFieldErrors = Record<string, string>;

type ManageGroupsDraftState = {
  rows: ManageGroupsDraftRow[];
  /**
   * The server state the rows were seeded from. Held in state rather than read
   * live so a background refetch of the groups query can't change what Save
   * will do while the user is mid-edit.
   */
  baselineGroups: QuoteOfferingGroup[];
  newRowCount: number;
};

type ManageGroupsDraftAction =
  | { type: 'SEED'; groups: Maybe<QuoteOfferingGroup[]> }
  | { type: 'RESET' }
  | { type: 'ADD_ROW'; quote: Maybe<IQuoteRespSchema> }
  | {
      type: 'UPDATE_FIELD';
      key: string;
      field: ManageGroupsEditableField;
      value: string;
    }
  | {
      type: 'ASSIGN_OFFERING';
      quoteOfferingId: string;
      toRowKey: string | null;
    }
  | {
      type: 'UPDATE_CUSTOM_FIELDS';
      key: string;
      customFields: ICustomFieldRecordSchema;
    }
  | { type: 'REMOVE_ROW'; key: string };

const withoutOffering = (
  row: ManageGroupsDraftRow,
  quoteOfferingId: string,
): ManageGroupsDraftRow =>
  row.quoteOfferingIds.includes(quoteOfferingId)
    ? {
        ...row,
        quoteOfferingIds: row.quoteOfferingIds.filter(
          (memberId) => memberId !== quoteOfferingId,
        ),
      }
    : row;

export const manageGroupsDraftReducer = (
  state: ManageGroupsDraftState,
  action: ManageGroupsDraftAction,
): ManageGroupsDraftState => {
  switch (action.type) {
    case 'SEED':
      return {
        rows: seedManageGroupsDraftRows(action.groups),
        baselineGroups: action.groups || [],
        newRowCount: 0,
      };

    /** Discard every staged edit and go back to the groups on the server */
    case 'RESET':
      return {
        ...state,
        rows: seedManageGroupsDraftRows(state.baselineGroups),
        newRowCount: 0,
      };

    case 'ADD_ROW':
      return {
        ...state,
        rows: [
          ...state.rows,
          buildNewManageGroupsDraftRow(
            action.quote,
            `new-${state.newRowCount + 1}`,
          ),
        ],
        newRowCount: state.newRowCount + 1,
      };

    case 'UPDATE_FIELD':
      return {
        ...state,
        rows: state.rows.map((row) => {
          if (row.key !== action.key) {
            return row;
          }
          const updatedRow = { ...row, [action.field]: action.value };
          // An account change invalidates its contact — same rule the group
          // drawer's account selects apply.
          if (action.field === 'shippingAccountId') {
            updatedRow.shippingContactId = '';
          }
          if (action.field === 'billingAccountId') {
            updatedRow.billingContactId = '';
          }
          return updatedRow;
        }),
      };

    /**
     * Claiming an offering for a row first takes it off every other row, so an
     * offering is never staged in two groups. `toRowKey: null` ungroups it.
     */
    case 'ASSIGN_OFFERING':
      return {
        ...state,
        rows: state.rows.map((row) => {
          const rowWithoutOffering = withoutOffering(
            row,
            action.quoteOfferingId,
          );
          return row.key === action.toRowKey
            ? {
                ...rowWithoutOffering,
                quoteOfferingIds: [
                  ...rowWithoutOffering.quoteOfferingIds,
                  action.quoteOfferingId,
                ],
              }
            : rowWithoutOffering;
        }),
      };

    case 'UPDATE_CUSTOM_FIELDS':
      return {
        ...state,
        rows: state.rows.map((row) =>
          row.key === action.key
            ? { ...row, customFields: action.customFields }
            : row,
        ),
      };

    /** Members of a removed row fall back to ungrouped; they stay on the quote */
    case 'REMOVE_ROW':
      return {
        ...state,
        rows: state.rows.filter(({ key }) => key !== action.key),
      };

    default:
      return state;
  }
};

const initState = (groups: Maybe<QuoteOfferingGroup[]>) => ({
  rows: seedManageGroupsDraftRows(groups),
  baselineGroups: groups || [],
  newRowCount: 0,
});

/**
 * Staged, in-memory model of every quote offering group on a quote. Nothing
 * here talks to the API — see useApplyQuoteOfferingGroupsDraft for the save.
 */
export const useManageQuoteOfferingGroupsDraft = ({
  quote,
  groups,
}: {
  quote: Maybe<IQuoteRespSchema>;
  groups: Maybe<QuoteOfferingGroup[]>;
}) => {
  const [state, dispatch] = useReducer(
    manageGroupsDraftReducer,
    groups,
    initState,
  );
  const { rows, baselineGroups } = state;
  const quoteOfferings = quote?.quoteOfferings;

  const seed = useCallback(
    (nextGroups: Maybe<QuoteOfferingGroup[]>) =>
      dispatch({ type: 'SEED', groups: nextGroups }),
    [],
  );

  const reset = useCallback(() => dispatch({ type: 'RESET' }), []);

  const addRow = useCallback(
    () => dispatch({ type: 'ADD_ROW', quote }),
    [quote],
  );

  const updateField = useCallback(
    (key: string, field: ManageGroupsEditableField, value: string) =>
      dispatch({ type: 'UPDATE_FIELD', key, field, value }),
    [],
  );

  const assignOffering = useCallback(
    (quoteOfferingId: string, toRowKey: string | null) =>
      dispatch({ type: 'ASSIGN_OFFERING', quoteOfferingId, toRowKey }),
    [],
  );

  const updateCustomFields = useCallback(
    (key: string, customFields: ICustomFieldRecordSchema) =>
      dispatch({ type: 'UPDATE_CUSTOM_FIELDS', key, customFields }),
    [],
  );

  const removeRow = useCallback(
    (key: string) => dispatch({ type: 'REMOVE_ROW', key }),
    [],
  );

  const rootQuoteOfferings = useMemo(
    () => getRootQuoteOfferings(quoteOfferings),
    [quoteOfferings],
  );

  /** Which staged row currently holds each offering */
  const rowKeyByOfferingId = useMemo(() => {
    const rowKeys = new Map<string, string>();
    rows.forEach(({ key, quoteOfferingIds }) => {
      quoteOfferingIds.forEach((quoteOfferingId) =>
        rowKeys.set(quoteOfferingId, key),
      );
    });
    return rowKeys;
  }, [rows]);

  const ungroupedOfferings = useMemo(
    () => rootQuoteOfferings.filter(({ id }) => !rowKeyByOfferingId.has(id)),
    [rootQuoteOfferings, rowKeyByOfferingId],
  );

  const offeringById = useMemo(
    () =>
      new Map<string, IQuoteOfferingRespSchema>(
        rootQuoteOfferings.map((quoteOffering) => [
          quoteOffering.id,
          quoteOffering,
        ]),
      ),
    [rootQuoteOfferings],
  );

  /**
   * Term + amount per offering, computed once here rather than per card — both
   * have to walk the offering list for ramp segments.
   */
  const offeringDetailById = useMemo(
    () =>
      new Map<string, ManageGroupsOfferingDetail>(
        rootQuoteOfferings.map((quoteOffering) => [
          quoteOffering.id,
          getQuoteOfferingDisplayDetail(
            quoteOffering,
            quoteOfferings,
            quote?.currency,
          ),
        ]),
      ),
    [rootQuoteOfferings, quoteOfferings, quote?.currency],
  );

  /**
   * Group total per staged row, so a card carries the same subtotal its
   * container on the quote does — and updates it as offerings are moved.
   */
  const rowSubtotalByKey = useMemo(
    () =>
      new Map<string, string>(
        rows.map(({ key, quoteOfferingIds }) => [
          key,
          getManageGroupsRowSubtotal(
            quoteOfferingIds,
            quoteOfferings,
            quote?.currency,
          ),
        ]),
      ),
    [rows, quoteOfferings, quote?.currency],
  );

  /**
   * Per-row validation borrowed from the group request schema itself, so the
   * required-field messages match what the rest of the app shows. Keyed by field
   * so each message can render under the input it belongs to.
   */
  const rowErrors = useMemo(() => {
    const errorsByRowKey = new Map<string, ManageGroupsRowFieldErrors>();
    rows.forEach((row) => {
      const result = QuoteOfferingGroupRequestSchema.safeParse(
        buildManageGroupsRowPayload(row, quoteOfferings),
      );
      if (!result.success) {
        const fieldErrors: ManageGroupsRowFieldErrors = {};
        result.error.issues.forEach(({ path, message }) => {
          const [field] = path;
          // First message per field wins; the rest say the same thing
          if (typeof field === 'string' && !fieldErrors[field]) {
            fieldErrors[field] = message;
          }
        });
        errorsByRowKey.set(row.key, fieldErrors);
      }
    });
    return errorsByRowKey;
  }, [rows, quoteOfferings]);

  const plan = useMemo(
    () =>
      buildManageGroupsDraftPlan({
        rows,
        serverGroups: baselineGroups,
        quoteOfferings,
      }),
    [rows, baselineGroups, quoteOfferings],
  );

  const rowStatusByKey = useMemo(
    () => getManageGroupsRowStatuses(plan),
    [plan],
  );

  return {
    rows,
    plan,
    rowStatusByKey,
    rowSubtotalByKey,
    rowErrors,
    offeringById,
    offeringDetailById,
    rootQuoteOfferings,
    ungroupedOfferings,
    hasChanges: hasManageGroupsDraftChanges(plan),
    isValid: rowErrors.size === 0,
    seed,
    reset,
    addRow,
    updateField,
    assignOffering,
    updateCustomFields,
    removeRow,
  };
};
